import React from 'react'
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  FlexProps,
  forwardRef,
  Heading,
  HeadingProps,
  IconButton,
  IconButtonProps,
  Link,
  LinkProps,
  Tooltip,
  createStylesContext,
  useMultiStyleConfig
} from '../../chakra'
import { RegionErrorBoundary } from '../empty_states/EmptyStates'

const [StylesProvider, useStyles] = createStylesContext('Card')

/**
 * @module Card
 *
 * This module contains `Card` and related components. These components can be
 * composed together to create flexible, reusable cards.
 */

// Chakra BoxProps types `variant` as the stock Card recipe values; omit it so
// our theme variants (`tonal`, …) type-check as plain strings.
export type CardProps = Omit<BoxProps, 'variant' | 'size'> & {
  variant?: string
  size?: string
}

/**
 * Card
 *
 * The main container for a card. Use the other components in this module
 * inside of `Card` to create a full card layout.
 *
 * It renders the card border. Legacy also has a shadow.
 *
 * Flag-OFF, Card has no padding. Compose with CardBody (see WithoutHeader).
 * Flag-ON, the container is p={6} and CardBody is p={0}.
 *
 * The underlying component is Chakra's `Box`.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-cards--docs
 *
 * @agent-gotcha use CardHeader/CardHeading/CardBody/CardFooter composition.
 * @agent-gotcha CardHeading defaults to `m={0}` so Bootstrap's h3 margin does not leak on Django mounts without `.isChakra`.
 * @agent-gotcha CardBody `p={0}` is for lists/tables/accordions sitting flush. Nested `p={6}` inside it double-pads under rebrand.
 */
export const Card = forwardRef<CardProps, 'div'>(({ children, variant, size, ...props }, ref) => {
  // Pull variant/size for the style config. Leave them out of the DOM.
  const styles = useMultiStyleConfig('Card', { variant, size })
  return (
    <Box __css={styles.container} role="region" data-card data-testid="Card" ref={ref} {...props}>
      <StylesProvider value={styles}>{children}</StylesProvider>
    </Box>
  )
})

Card.displayName = 'Card'

/**
 * Card Header (Wrapper)
 *
 * The header for a `Card`. It renders a space at the top with a bottom border.
 *
 * The underlying component is Chakra's `Flex`.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-cards--docs
 */
export const CardHeader = forwardRef((props: FlexProps, ref) => {
  const styles = useStyles()
  return <Flex as="header" __css={styles.header} data-card-header ref={ref} {...props} />
})

CardHeader.displayName = 'CardHeader'

/**
 * Card Footer
 *
 * The footer for a `Card`. It renders a space at the bottom with a top border.
 *
 * The underlying component is Chakra's `Flex`.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-cards--docs
 */
export const CardFooter = forwardRef((props: FlexProps, ref) => {
  const styles = useStyles()
  return <Flex as="footer" __css={styles.footer} data-card-footer ref={ref} {...props} />
})

CardFooter.displayName = 'CardFooter'

/**
 * Card Header Title
 *
 * The heading text for a `Card`. Use this inside of `CardHeader`.
 *
 * The underlying component is Chakra's `Heading`.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-cards--docs
 *
 * `m={0}` kills Bootstrap's `h3 { margin-top: 20px }` on Django mounts that skip
 * `.isChakra`. Override via props if you need a margin.
 */
export const CardHeading = forwardRef((props: HeadingProps, ref) => (
  <Heading as="h3" textStyle="heading4" data-card-heading flexGrow={1} m={0} ref={ref} {...props} />
))

CardHeading.displayName = 'CardHeading'

/**
 * Card Header with Link Button
 *
 * An action button for a `Card`. Use this inside of `CardHeader`. The action button will be a link
 * button with the ability to have a click event. It will automatically push itself
 * to the right side of the header.
 *
 * The underlying element is Chakra's `Button`.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-cards--docs
 */
export const CardActionButton = forwardRef((props: ButtonProps, ref) => (
  <Button variant="inlineLink" ref={ref} {...props} />
))

CardActionButton.displayName = 'CardActionButton'

/**
 * Card Header with Link
 *
 * An action link for a `Card`. Use this inside of `CardHeader`. The action link will look exactly like
 * the action link button but give the ability to act as a link with a href. It will automatically push
 * itself to the right side of the header.
 *
 * The underlying element is Chakra's `Link`.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-cards--docs
 */
export const CardActionLink = forwardRef((props: LinkProps, ref) => <Link variant="link" ref={ref} {...props} />)

CardActionLink.displayName = 'CardActionLink'

/**
 * Card Header with Icon
 *
 * An action icon for a `Card`. Use this inside of `CardHeader`. It will automatically push itself to the right
 * side of the header.
 *
 * The underlying element is Chakra's `IconButton`. Use the `icon` prop to set the
 * icon. You must provide an `aria-label`.
 *
 * Note: We are no longer using Material UI icons. Please refer to our custom icons:
 * https://storybook.beta.vndly.com/?path=/docs/foundations-icons-icons--page
 */
export const CardActionIcon = forwardRef(({ 'aria-label': ariaLabel, ...props }: IconButtonProps, ref) => (
  <Tooltip aria-label={ariaLabel} label={ariaLabel}>
    <IconButton
      aria-label={ariaLabel}
      variant="inlineGray"
      size="md"
      fontSize="1.5rem"
      color="gray.600"
      cursor="pointer"
      _hover={{ color: 'gray.900' }}
      my="-8px"
      ml={2}
      _last={{ mr: '-8px' }}
      ref={ref}
      {...props}
    />
  </Tooltip>
))

CardActionIcon.displayName = 'CardActionIcon'

/**
 * Card Body
 *
 * The body of a `Card`.
 *
 * Flag-OFF default is p={6}. Pass p={0} for lists, tables, accordions.
 * Flag-ON body is already p={0}; the container holds the inset.
 * Nested p={6} inside p={0} double-pads under rebrand.
 *
 * The underlying element is Chakra's `Box`.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-cards--docs
 */
export const CardBody = forwardRef<BoxProps, 'div'>(({ children, ...props }: BoxProps, ref) => {
  const styles = useStyles()

  return (
    <Box ref={ref} __css={styles.body} data-card-body {...props}>
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </Box>
  )
})

CardBody.displayName = 'CardBody'
